import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { useDarkMode } from '../../hooks/useDarkMode';
import { canAccess } from '../../utils/rbac';
import { CAUSES, STATUS_COLORS, Pill, ACTIVE_STAGES, OUTCOME_STATUSES } from './BidTrackerPage';

// BD Dashboard — summarises the Bid Tracker (`bids`), the team's live pipeline.
// Tender + news counts and upcoming tender deadlines come from /api/crm/dashboard.

export default function CrmDashboardPage({ onNavigate }) {
  const isDark = useDarkMode();
  const [bids, setBids] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [bidData, dash] = await Promise.all([
        api.get('crm/bids', { kind: 'bid' }),
        api.get('crm/dashboard').catch(() => ({})),
      ]);
      setBids(bidData?.rows || []);
      setMeta(dash || {});
    } catch (e) {
      setError(e?.message || 'Failed to load the dashboard.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ padding: 24, fontSize: 11, color: '#6B7280' }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 24, fontSize: 11, color: '#EF4444' }}>
      {error}
      <button onClick={load} style={{ marginLeft: 10, padding: '4px 10px', fontSize: 9, border: '1px solid #E5E7EB', borderRadius: 5, cursor: 'pointer', background: '#fff' }}>Retry</button>
    </div>
  );

  // ── Aggregate the bid pipeline ────────────────────────────────────────────
  const live = bids.filter(b => ACTIVE_STAGES.includes(b.status));
  const won = bids.filter(b => b.status === 'Won');
  const lost = bids.filter(b => b.status === 'Lost');
  const decided = bids.filter(b => b.result);
  const upcoming = bids.filter(b => b.status === 'Upcoming');

  // Status breakdown (exclude Upcoming — it has its own card)
  const STATUS_ORDER = [...ACTIVE_STAGES, ...OUTCOME_STATUSES];
  const statusCounts = STATUS_ORDER
    .map(s => ({ status: s, count: bids.filter(b => b.status === s).length }))
    .filter(r => r.count > 0);
  const maxStatus = Math.max(1, ...statusCounts.map(r => r.count));

  // Win/Loss root-cause frequency
  const freq = CAUSES.map(c => ({ ...c, n: decided.filter(b => b[c.key]).length }));
  const maxCause = Math.max(1, ...freq.map(f => f.n));

  // Certifications expiring soon / already expired (server returns those due within 60 days).
  const certsExpiring = meta.certs_expiring || [];
  const certDaysLeft = (d) => (d ? Math.ceil((new Date(d + 'T00:00:00') - Date.now()) / 86400000) : null);
  const certExpired = certsExpiring.filter(c => (certDaysLeft(c.expiry_date) ?? 0) < 0).length;
  const certSoon = certsExpiring.length - certExpired;

  const cardStyle = { background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, padding: 14 };
  const panelTitle = { fontSize: 10, fontWeight: 700, color: isDark ? 'rgb(226 232 240)' : '#374151', marginBottom: 10 };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 15, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827', margin: 0 }}>Bids</h1>
        <p style={{ fontSize: 10, color: '#6B7280', margin: '2px 0 0' }}>Bid pipeline · win/loss · certifications</p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Live Bids', value: live.length, sub: 'active + submitted', color: '#F97316', onClick: () => onNavigate?.('bid-tracker') },
          { label: 'Won / Lost', value: `${won.length} / ${lost.length}`, sub: `${decided.length} decided`, color: '#22C55E', onClick: () => onNavigate?.('bid-tracker') },
          { label: 'Certs Expiring', value: certSoon, sub: 'within 60 days', color: '#A16207', onClick: () => onNavigate?.(canAccess('qms-admin') ? 'qms-admin' : 'qms-library'), badge: certSoon > 0 },
          { label: 'Certs Expired', value: certExpired, sub: 'need renewal', color: '#EF4444', onClick: () => onNavigate?.(canAccess('qms-admin') ? 'qms-admin' : 'qms-library'), badge: certExpired > 0 },
        ].map(card => (
          <div key={card.label} onClick={card.onClick}
            style={{ ...cardStyle, padding: '12px 14px', cursor: 'pointer', position: 'relative' }}
          >
            {card.badge && (
              <div style={{ position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />
            )}
            <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(226 232 240)' : '#374151', marginTop: 2 }}>{card.label}</div>
            <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 1 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
        {/* Bid pipeline by status */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={panelTitle}>Bid Pipeline by Status</div>
            <button onClick={() => onNavigate?.('bid-tracker')} style={{ fontSize: 8, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>View tracker →</button>
          </div>
          {statusCounts.length === 0 && <div style={{ fontSize: 9, color: '#9CA3AF' }}>No bids yet</div>}
          {statusCounts.map(row => {
            const c = STATUS_COLORS[row.status] || { bg: '#F9FAFB', color: '#374151' };
            return (
              <div key={row.status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 9, background: c.bg, color: c.color, padding: '2px 7px', borderRadius: 12, fontWeight: 700, minWidth: 64, textAlign: 'center' }}>{row.status}</span>
                <div style={{ flex: 1, margin: '0 10px', height: 4, background: isDark ? 'rgb(51 65 85)' : '#F3F4F6', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: c.color, borderRadius: 2, width: `${(row.count / maxStatus) * 100}%`, opacity: 0.5 }} />
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827', minWidth: 20, textAlign: 'right' }}>{row.count}</span>
              </div>
            );
          })}
          {upcoming.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}`, fontSize: 8, color: '#6B7280' }}>
              Plus <strong style={{ color: '#4F46E5' }}>{upcoming.length}</strong> upcoming (not yet live).
            </div>
          )}
        </div>

        {/* Win/Loss root causes */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={panelTitle}>Loss Root Causes</div>
            <span style={{ fontSize: 8, color: '#9CA3AF' }}>{decided.length} decided</span>
          </div>
          {decided.length === 0 && <div style={{ fontSize: 9, color: '#9CA3AF' }}>No decided bids yet</div>}
          {decided.length > 0 && freq.map(f => (
            <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 150, fontSize: 8, color: isDark ? 'rgb(203 213 225)' : '#374151' }}>{f.short}</div>
              <div style={{ flex: 1, height: 5, background: isDark ? 'rgb(51 65 85)' : '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(f.n / maxCause) * 100}%`, background: '#1B2A4A', borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827', minWidth: 16, textAlign: 'right' }}>{f.n}</span>
            </div>
          ))}
        </div>

        {/* Certifications expiring or expired — renewals to chase */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={panelTitle}>Expiring Certifications</div>
            <button onClick={() => onNavigate?.(canAccess('qms-admin') ? 'qms-admin' : 'qms-library')} style={{ fontSize: 8, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>View all →</button>
          </div>
          {certsExpiring.length === 0 && (
            <div style={{ fontSize: 9, color: '#9CA3AF' }}>Nothing expiring in the next 60 days</div>
          )}
          {certsExpiring.map(c => {
            const daysLeft = certDaysLeft(c.expiry_date);
            return (
              <div key={c.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F9FAFB'}` }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: isDark ? 'rgb(241 245 249)' : '#111827', lineHeight: 1.3 }}>{c.name}</div>
                <div style={{ fontSize: 8, color: '#6B7280', marginTop: 2 }}>
                  {c.issuing_body && <span>{c.issuing_body} · </span>}
                  {daysLeft !== null && (
                    daysLeft < 0
                      ? <span style={{ color: '#EF4444', fontWeight: 600 }}>expired {Math.abs(daysLeft)}d ago</span>
                      : <span style={{ color: daysLeft <= 14 ? '#EF4444' : daysLeft <= 30 ? '#F59E0B' : '#6B7280', fontWeight: 600 }}>{daysLeft}d left</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
